import React from 'react';
import styled from 'styled-components';
import { media } from '../theme/tokens';

interface AlertBannerProps {
  title: string;
  description: string;
  onClick?: () => void;
  unavailable?: boolean;
}

export const AlertBanner: React.FC<AlertBannerProps> = ({
  title,
  description,
  onClick,
  unavailable = false
}) => {
  if (unavailable) {
    return (
      <BannerWrap $unavailable>
        <IconWrap>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
        </IconWrap>
        <Content>
          <Title>Alert data unavailable</Title>
          <Desc>
            This section requires an API endpoint not yet implemented.
          </Desc>
        </Content>
      </BannerWrap>
    );
  }

  return (
    <BannerWrap onClick={onClick} $unavailable={false}>
      <IconWrap>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" />
        </svg>
      </IconWrap>
      <Content>
        <Title>{title}</Title>
        <Desc>{description}</Desc>
      </Content>
      <Arrow>→</Arrow>
    </BannerWrap>
  );
};

const BannerWrap = styled.div<{ $unavailable: boolean }>`
  background: ${({ $unavailable }) =>
    $unavailable
      ? 'linear-gradient(90deg, var(--paper) 0%, var(--paper) 60%)'
      : 'linear-gradient(90deg, var(--crimson-soft) 0%, var(--paper) 60%)'};
  border: 1px solid
    ${({ $unavailable }) =>
      $unavailable ? 'var(--line)' : 'var(--crimson-soft)'};
  border-left: 4px solid
    ${({ $unavailable }) =>
      $unavailable ? 'var(--ink-faint)' : 'var(--crimson)'};
  border-radius: var(--radius-md);
  padding: 12px 16px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: ${({ $unavailable }) => ($unavailable ? 'default' : 'pointer')};
  transition: all 0.15s;
  opacity: ${({ $unavailable }) => ($unavailable ? 0.5 : 1)};
  filter: ${({ $unavailable }) => ($unavailable ? 'grayscale(0.6)' : 'none')};

  &:hover {
    box-shadow: ${({ $unavailable }) =>
      $unavailable ? 'none' : 'var(--shadow-sm)'};
  }

  ${media.md} {
    padding: 14px 20px;
  }
`;

const IconWrap = styled.div`
  width: 32px;
  height: 32px;
  background: var(--white);
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: var(--crimson);
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 2px;
`;

const Desc = styled.div`
  font-size: 13px;
  color: var(--ink-muted);
`;

const Arrow = styled.div`
  color: var(--crimson);
  font-size: 16px;
`;
